// src/pages/Candidates.js
import React, { useState, useEffect } from 'react';
import { Globe, Loader, AlertTriangle } from 'lucide-react';
import { fetchCandidates } from '../utils/airtable';
import './Candidates.css';

const LEVELS = ['Federal', 'Statewide', 'Legislative', 'County', 'Municipal'];
const ELECTIONS = ['Primary 2026', 'General 2026'];

const CandidateCard = ({ candidate, activeElection }) => {
  const initials = `${candidate['First Name']?.[0] || ''}${candidate['Last Name']?.[0] || ''}`;
  const photoUrl = candidate.Photo?.[0]?.url;
  const bgColor = candidate.Party === 'Republican' ? '#ae3030'
  : candidate.Party === 'Democrat' ? '#182B45'
  : '#5f5e5a';

  return (
    <div className={`cand-card ${candidate.Status === 'Incumbent' ? 'incumbent' : ''}`}>
      <div className="cand-top">
        <div className="cand-av" style={{ background: photoUrl ? 'transparent' : bgColor }}>
          {photoUrl
            ? <img src={photoUrl} alt={candidate.Name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            : initials
          }
        </div>
        <div>
          <div className="cand-name">{candidate.Name}</div>
          <div className="cand-party-text">{candidate.Party}</div>
        </div>
      </div>

      <div className="cand-details">
        {candidate.Office && (
          <div className="cand-detail-row">
            <span className="cand-detail-label">Office</span>
            <span className="cand-detail-value">{candidate.Office}</span>
          </div>
        )}
        {candidate.District && (
          <div className="cand-detail-row">
            <span className="cand-detail-label">District</span>
            <span className="cand-detail-value">{candidate.District}</span>
          </div>
        )}
        {(activeElection || candidate['Election Type']) && (
          <div className="cand-detail-row">
            <span className="cand-detail-label">Election</span>
            <span className="cand-detail-value">{activeElection || candidate['Election Type']}</span>
          </div>
        )}
        {candidate['Primary Votes Received'] != null && (
          <div className="cand-detail-row">
            <span className="cand-detail-label">Primary Votes Received</span>
            <span className="cand-detail-value">{candidate['Primary Votes Received']}</span>
          </div>
        )}
      </div>

      <div className="cand-badges">
       {candidate.Party && <span className="badge badge-red">{candidate.Party}</span>}
       {candidate.Status && <span className="badge badge-gray">{candidate.Status}</span>}
      </div>

      {candidate.Bio && <div className="cand-bio">{candidate.Bio}</div>}

      <div className="cand-links">
        {candidate.Website && <a className="cand-link red" href={candidate.Website} target="_blank" rel="noreferrer"><Globe size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Website</a>}
        {candidate.Twitter && <a className="cand-link" href={candidate.Twitter} target="_blank" rel="noreferrer">𝕏 Twitter</a>}
        {candidate.Facebook && <a className="cand-link" href={candidate.Facebook} target="_blank" rel="noreferrer">f Facebook</a>}
      </div>
    </div>
  );
};
const Candidates = ({ county }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeLevel, setActiveLevel] = useState('Federal');
  const [activeElection, setActiveElection] = useState('General 2026');

  useEffect(() => {
    fetchCandidates()
      .then(data => { setCandidates(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const parseVotePct = (str) => {
    if (!str) return -1;
    const match = str.match(/^(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : -1;
  };

  const filtered = candidates
    .filter(c => {
      if (c.Active !== true || c.Level !== activeLevel) return false;
      // Primary winners advance to the General ballot even if their
      // Election Type field is still marked Primary 2026.
      if (activeElection === 'General 2026') {
        return c['Election Type'] === 'General 2026' || c['Won Primary'] === true;
      }
      return c['Election Type'] === activeElection;
    })
    .sort((a, b) => {
      const officeA = `${a.Office}|${a.District}`;
      const officeB = `${b.Office}|${b.District}`;
      if (officeA !== officeB) return officeA.localeCompare(officeB);
      return parseVotePct(b['Primary Votes Received']) - parseVotePct(a['Primary Votes Received']);
    });

  return (
    <div>
      <div className="sec-head">
        <div>
          <div className="sec-title">Candidates &amp; Races</div>
          <div className="sec-desc">2026 Republican candidates in {county?.name || 'Larimer County'} races</div>
        </div>
      </div>

      {activeElection === 'Primary 2026' && (
        <div className="primary-banner">
          <strong>Official Primary Election Results as of 7/29/2026.</strong>
        </div>
      )}

      <div className="filter-bar">
        <span className="filter-label">Election:</span>
        <div className="election-toggle">
          {ELECTIONS.map(e => (
            <button key={e} className={`etog ${activeElection === e ? 'active' : ''}`} onClick={() => setActiveElection(e)}>
              {e}
            </button>
          ))}
        </div>
      </div>

      <div className="level-tabs">
        {LEVELS.map(level => (
          <button key={level} className={`level-tab ${activeLevel === level ? 'active' : ''}`} onClick={() => setActiveLevel(level)}>
            {level}
          </button>
        ))}
      </div>

      {loading && <div className="loading"><Loader size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Loading candidates...</div>}
      {error && <div className="error-msg"><AlertTriangle size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Could not load candidates. Please check your Airtable connection.<br/><small>{error}</small></div>}

      {!loading && !error && filtered.length === 0 && (
        <div className="no-results">
          No {activeElection} candidates found for {activeLevel} races yet. Check back as the election approaches or visit the <a href="https://www.sos.state.co.us/pubs/elections/Candidates/CandidateHome.html" target="_blank" rel="noreferrer">CO SOS candidate list</a>.
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="cand-grid">
          {filtered.map(c => <CandidateCard key={c.id} candidate={c} activeElection={activeElection} />)}
        </div>
      )}
    </div>
  );
};

export default Candidates;
